import { create } from "zustand";
import { api, ApiError } from "../utils/api";
import { deletionDetail } from "../utils/accountDeletion";
import type { User } from "../types";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  _checked: boolean;
  /** #398: the account signed in with the right password is scheduled for deletion.
   *  Only the email and the date are held here; the password stays in the form. */
  pendingDeletion: { email: string; scheduledFor: string } | null;
  /** #398: the date of a deletion just requested in Settings, for the sign-in notice.
   *  Kept here, not in the URL; the protected route's redirect would drop router state. */
  deletedUntil: string | null;

  checkAuth: (force?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  exitImpersonation: () => Promise<void>;
  updatePreferences: (patch: { units?: "standard" | "metric" }) => Promise<void>;
  loginWithIracing: (opts?: { inviteCode?: string; returnTo?: string }) => void;
  login: (email: string, password: string) => Promise<void>;
  /** Restore an account pending deletion, then sign in (#398). */
  restore: (email: string, password: string) => Promise<void>;
  clearPendingDeletion: () => void;
  /** The server already revoked every session (#398): drop the user without a
   *  logout call, which would 401. */
  markSignedOut: (deletedUntil?: string) => void;
  register: (inviteCode: string, iracingCustId: string, displayName: string, email: string, password: string) => Promise<void>;
  registerTeam: (email: string, password: string, teamName: string, displayName?: string) => Promise<string | null>;
  registerIndividual: (email: string, password: string, displayName?: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  _checked: false,
  pendingDeletion: null,
  deletedUntil: null,

  checkAuth: async (force = false) => {
    if (get()._checked && !force) return;
    // #803: a re-check while someone is already signed in (after a profile or avatar
    // save) refreshes the user quietly. Flipping the global `loading` made
    // ProtectedRoute swap in its spinner, which remounted the page and lost the
    // "saved" message it was about to show.
    set({ loading: !get().user, error: null });
    try {
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 401) {
        try {
          await api("/api/auth/refresh", { method: "POST" });
          const data = await api<User>("/api/auth/me");
          set({ user: data, loading: false, _checked: true });
          return;
        } catch {
          set({ user: null, loading: false, _checked: true });
        }
      } else {
        set({ error: apiErr.message || "Auth check failed", user: null, loading: false, _checked: true });
      }
    }
  },

  // End an admin "view as team" support session. Errors are ignored — the grant
  // is short-lived server-side either way; the caller hard-navigates after this.
  exitImpersonation: async () => {
    try {
      await api("/api/admin/impersonation/exit", { method: "POST" });
    } catch { /* ignore */ }
    set((state) => ({
      user: state.user ? { ...state.user, viewingAs: null } : state.user,
    }));
  },

  logout: async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch { /* ignore */ }
    set({ user: null, error: null, loading: false, _checked: true });
    window.location.href = "/login";
  },

  updatePreferences: async (patch) => {
    if (!get().user) return;
    set({ loading: true, error: null });
    try {
      const result = await api<{ preferences: User["preferences"] }>("/api/auth/preferences", {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      set((state) => ({
        user: state.user
          ? {
              ...state.user,
              preferences: result.preferences,
            }
          : state.user,
        loading: false,
        _checked: true,
      }));
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      set({ error: apiErr.message || "Failed to save preferences", loading: false });
      throw err;
    }
  },

  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/login-simple", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        const d = deletionDetail(err.body);
        if (d.code === "deletion_pending" && d.deletionScheduledFor) {
          // A state, not an error: the panel replaces the form (design council).
          set({ pendingDeletion: { email, scheduledFor: d.deletionScheduledFor }, error: null, loading: false });
          throw err;
        }
      }
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Login failed", loading: false });
      throw err;
    }
  },

  restore: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/restore", { method: "POST", body: JSON.stringify({ email, password }) });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
    // Restored, whatever happens next: the panel must never say "scheduled" again, and
    // a retry would only meet "not pending deletion" (Codex, PR #898).
    set({ pendingDeletion: null });
    try {
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch {
      set({ loading: false, error: "Your account is restored. Sign in to continue." });
    }
  },

  clearPendingDeletion: () => set({ pendingDeletion: null, error: null }),

  markSignedOut: (deletedUntil) => set({ user: null, _checked: true, deletedUntil: deletedUntil ?? null }),

  register: async (inviteCode: string, iracingCustId: string, displayName: string, email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ inviteCode, iracingCustId, displayName, email, password }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Registration failed", loading: false });
      throw err;
    }
  },

  // Self-serve team signup (#253A/#254): create a team, free in early access,
  // no invite code, no iRacing customer ID.
  registerTeam: async (email: string, password: string, teamName: string, displayName?: string) => {
    set({ loading: true, error: null });
    try {
      const created = await api<{ teamId?: string }>("/api/auth/register-team", {
        method: "POST",
        body: JSON.stringify({ email, password, teamName, displayName }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
      return created?.teamId ?? null;   // #743: sign-up lands on the new team
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Sign-up failed", loading: false });
      throw err;
    }
  },

  // Solo driver signup (SimSense merge Phase 1): free individual account,
  // backend creates a hidden comped personal team behind the scenes.
  registerIndividual: async (email: string, password: string, displayName?: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/register-individual", {
        method: "POST",
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Sign-up failed", loading: false });
      throw err;
    }
  },

  loginWithIracing: (opts) => {
    set({ loading: true, error: null });
    const params = new URLSearchParams();
    const inviteCode = opts?.inviteCode?.trim();
    const returnTo = opts?.returnTo?.trim();
    if (inviteCode) params.set("invite_code", inviteCode);
    if (returnTo) params.set("return_to", returnTo);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    window.location.href = `/api/auth/iracing/login${suffix}`;
  },
}));
